import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Rating,
    Box,
    Typography,
    CircularProgress,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import {
    hideSurvey,
    setRating,
    setFeedback,
    submitFeedback,
    checkSurveyStatus
} from './FeedbackSlice';

export default function FeedbackSurvey() {
    const dispatch = useDispatch();
    const {
        showSurvey,
        shouldShowSurvey,
        rating,
        feedback,
        submitting,
        submitted,
        currentPage
    } = useSelector(state => state.feedback);

    const { user } = useSelector(state => state.auth);
    const [requestFollowUp, setRequestFollowUp] = React.useState(false);

    useEffect(() => {
        if (user?.uid) {
            dispatch(checkSurveyStatus(user.uid));
        }
    }, [user?.uid, dispatch]);

    const handleClose = () => {
        dispatch(hideSurvey());
    };

    const handleSubmit = () => {
        dispatch(submitFeedback({
            rating,
            feedback,
            userId: user?.uid,
            requestFollowUp,
            userEmail: user?.email,
            currentPage
        }));
    };

    const generateEmailContent = () => {
        const subject = encodeURIComponent('CueMeUp Feedback');
        const body = encodeURIComponent(
            `Hi CueMeUp Team,

I'd like to share some feedback about the application.

Feedback:
<ENTER YOUR FEEDBACK HERE>

I would like to receive a follow-up about my feedback: yes/no

--- Site Information ---
Current Page: ${currentPage || window.location.pathname || 'Unknown'}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
Timestamp: ${new Date().toLocaleString()}



Best regards`
        );
        return `mailto:contact@cuemeup.com?subject=${subject}&body=${body}`;
    };

    // Don't show survey if it shouldn't be shown based on timing rules
    if (!showSurvey || !shouldShowSurvey) return null;

    return (
        <Dialog open={true} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Feedback Survey</DialogTitle>
            <DialogContent>
                {submitted ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography>Thank you for your feedback!</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        {user ? (
                            <>
                                <Box>
                                    <Typography component="legend">How would you rate your experience?</Typography>
                                    <Rating
                                        value={rating}
                                        onChange={(event, newValue) => {
                                            dispatch(setRating(newValue));
                                        }}
                                    />
                                </Box>
                                <TextField
                                    label="Additional Feedback"
                                    multiline
                                    rows={4}
                                    value={feedback}
                                    onChange={(e) => dispatch(setFeedback(e.target.value))}
                                    fullWidth
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={requestFollowUp}
                                            onChange={(e) => setRequestFollowUp(e.target.checked)}
                                        />
                                    }
                                    label="I'd like to receive a follow-up about my feedback"
                                />
                            </>
                        ) : (
                            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                                <Typography variant="h6" gutterBottom>
                                    You're not signed in
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Choose how you'd like to submit your feedback:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        href={generateEmailContent()}
                                        sx={{ minWidth: 200 }}
                                    >
                                        Send Feedback via Email
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        or
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        href="/auth"
                                        target="_blank"
                                        sx={{ minWidth: 200 }}
                                    >
                                        Sign In to Use Form
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {!submitted && (
                    <>
                        <Button onClick={handleClose}>Cancel</Button>
                        {user && (
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={submitting || rating === 0}
                            >
                                {submitting ? <CircularProgress size={24} /> : 'Submit'}
                            </Button>
                        )}
                    </>
                )}
                {submitted && (
                    <Button onClick={handleClose} variant="contained">
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
} 